import { useState } from "react";
import "./VesselSearch.css";

/*
  VesselSearch.jsx — Optional vessel filter (Step 1)

  Runs before file upload. Vessel input is optional:
  - Leave blank — parser loads all vessels from the file
  - Enter MMSI or name — parser filters to matching vessels only

  Free tier: one input field, optional
  Pro tier: up to 5 vessel targets (required when uploading multiple files)
*/

export default function VesselSearch({ onTargetsChange, unlocked, mode }) {
  // Each target is a string the user typed — could be MMSI or name
  const [inputs, setInputs] = useState([""]);

  const maxTargets = unlocked ? 5 : 1;

  // Update a specific input field
  function handleChange(index, value) {
    const updated = [...inputs];
    updated[index] = value;
    setInputs(updated);
    emitTargets(updated);
  }

  // Add another vessel input (paid tier only, up to 5)
  function addTarget() {
    if (inputs.length >= maxTargets) return;
    const updated = [...inputs, ""];
    setInputs(updated);
    emitTargets(updated);
  }

  // Remove a vessel input
  function removeTarget(index) {
    if (inputs.length === 1) return; // Always keep at least one input
    const updated = inputs.filter((_, i) => i !== index);
    setInputs(updated);
    emitTargets(updated);
  }

  // Convert input strings to target objects for the parser
  // { mmsi } if it looks like a number, { vesselName } if it's text
  function emitTargets(inputList) {
    const targets = inputList
      .map((val) => val.trim())
      .filter((val) => val.length > 0)
      .map((val) => {
        // If it's all digits, treat as MMSI
        if (/^\d+$/.test(val)) {
          return { mmsi: val };
        }
        return { vesselName: val };
      });

    onTargetsChange(targets);
  }

  return (
    <div className="search-section">
      <h2 className="section-title">
        Step 1 — {mode === "multi" ? "Which vessels are you tracking?" : "Which vessel are you looking for?"}
        {" "}<span className="optional-label">{mode === "multi" ? "(required)" : "(optional)"}</span>
      </h2>
      <p className="section-hint">
        {mode === "multi"
          ? "Enter at least one MMSI or vessel name. Required for multi-file mode to protect memory."
          : "Leave blank to see all vessels in the file. Enter an MMSI or name to filter to a specific vessel."}
        {unlocked && mode !== "multi" && inputs.length === 1
          ? " Add up to 5 targets when using multiple files."
          : ""}
      </p>

      <div className="search-targets">
        {inputs.map((val, index) => (
          <div key={index} className="search-target-row">
            <input
              className="search-input"
              type="text"
              placeholder={mode === "multi"
                ? "e.g. 367123456 or ATLANTIC VOYAGER"
                : "e.g. 367123456 or ATLANTIC VOYAGER — or leave blank"}
              value={val}
              onChange={(e) => handleChange(index, e.target.value)}
            />
            {inputs.length > 1 && (
              <button
                className="search-remove-button"
                onClick={() => removeTarget(index)}
                title="Remove"
              >
                ✕
              </button>
            )}
          </div>
        ))}
      </div>

      {unlocked && inputs.length < maxTargets && (
        <button className="search-add-button" onClick={addTarget}>
          + Add another vessel
        </button>
      )}
    </div>
  );
}
